import { infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/react-query";

import type { ActivityView } from "@/lib/activity/describe";
import { rangeMs, type BucketCount, type TimeRange } from "@/lib/activity/volume";

/** Rows per page of the audit log. */
const PAGE_SIZE = 100;

/** Rows beside an event in its "same person" timeline. */
const AROUND_LIMIT = 40;
const AROUND_WINDOW_MS = 30 * 60_000;

/** Reads a JSON answer, turning any failure into the user-facing message. */
async function readJson<T>(response: Response, message: string): Promise<T> {
  if (!response.ok) throw new Error(message);

  // SAFETY: every caller reads one of this app's own admin endpoints and names its answer shape.
  return (await response.json()) as T;
}

/** What narrows the journal besides its time window. */
export interface ActivityScope {
  view: ActivityView;
  search: string;
  /** Server params from the filter bar, e.g. `["outcome", "denied,failed"]`. */
  filters: readonly (readonly [string, string])[];
}

function scopeParams(scope: ActivityScope): URLSearchParams {
  const params = new URLSearchParams({ view: scope.view });

  if (scope.search) params.set("search", scope.search);

  for (const [key, value] of scope.filters) params.set(key, value);

  return params;
}

export interface ActivityWindow {
  range: TimeRange;
  /** ISO bounds of a chart selection, when one narrows the list. */
  selection: { startDate: string; endDate: string } | null;
}

export interface ActivityPageResponse<Row> {
  data: Row[];
  nextCursor: number | null;
  /** First page only. */
  counts: Record<ActivityView, number> | null;
}

/** The journal rows, newest first, paged by the id of the last row seen. */
export function activityListQueryOptions<Row>(
  scope: ActivityScope,
  window: ActivityWindow,
  now: number,
) {
  return infiniteQueryOptions({
    queryKey: ["admin", "activity", "list", scope, window, now],
    // 0 = no cursor: the first page.
    initialPageParam: 0,
    placeholderData: keepPreviousData,
    queryFn: async ({ pageParam }): Promise<ActivityPageResponse<Row>> => {
      const params = scopeParams(scope);
      params.set("limit", String(PAGE_SIZE));

      const start =
        window.selection?.startDate ?? new Date(now - rangeMs(window.range)).toISOString();

      params.set("startDate", start);

      if (window.selection) params.set("endDate", window.selection.endDate);

      if (pageParam) params.set("before", String(pageParam));

      return readJson(
        await fetch(`/api/admin/activity?${params}`, { cache: "no-store" }),
        "Impossible de charger le journal.",
      );
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

export interface ActivitySummary {
  range: TimeRange;
  windowEnd: number;
  offsetMs: number;
  buckets: BucketCount[];
  priorTotal: number;
}

/** Volume per bucket and outcome under the same scope as the list. */
export function activitySummaryQueryOptions(
  scope: ActivityScope,
  range: TimeRange,
  offsetMs: number,
) {
  return queryOptions({
    queryKey: ["admin", "activity", "summary", scope, range, offsetMs],
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }): Promise<ActivitySummary> => {
      const params = scopeParams(scope);
      params.set("range", range);
      params.set("offset", String(offsetMs));

      return readJson(
        await fetch(`/api/admin/activity/summary?${params}`, { cache: "no-store", signal }),
        "Impossible de charger le volume d'activité.",
      );
    },
  });
}

/** Refused or failed attempts in the window, newest first: the queue beside the chart. */
export function activityAttemptsQueryOptions<Row>(
  range: TimeRange,
  selection: ActivityWindow["selection"],
  now: number,
) {
  return queryOptions({
    queryKey: ["admin", "activity", "attempts", range, selection, now],
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<ActivityPageResponse<Row>> => {
      const params = new URLSearchParams({
        view: "errors",
        outcome: "denied,failed",
        limit: "50",
        startDate: selection?.startDate ?? new Date(now - rangeMs(range)).toISOString(),
      });

      if (selection) params.set("endDate", selection.endDate);

      return readJson(
        await fetch(`/api/admin/activity?${params}`, { cache: "no-store" }),
        "Impossible de charger les refus.",
      );
    },
  });
}

/** Everything one person did within half an hour of an event: the sheet's timeline. */
export function activityAroundQueryOptions<Row>(userId: string, at: string) {
  return queryOptions({
    queryKey: ["admin", "activity", "around", userId, at],
    queryFn: async (): Promise<ActivityPageResponse<Row>> => {
      const center = Date.parse(at);

      const params = new URLSearchParams({
        view: "all",
        userId,
        limit: String(AROUND_LIMIT),
        startDate: new Date(center - AROUND_WINDOW_MS).toISOString(),
        endDate: new Date(center + AROUND_WINDOW_MS).toISOString(),
      });

      return readJson(
        await fetch(`/api/admin/activity?${params}`, { cache: "no-store" }),
        "Impossible de charger l'activité voisine.",
      );
    },
  });
}
